import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useContent, formatPrice } from "@/context/ContentContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { User as UserIcon, Package, XCircle, Save, LogOut, ChevronRight, ClipboardCopy } from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-700",
  processing: "bg-sky-100 text-sky-700",
  shipped: "bg-orange-100 text-orange-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  refunded: "bg-purple-100 text-purple-700",
  paid: "bg-green-100 text-green-700",
};

const CANCELLABLE = new Set(["pending", "processing"]);
const CANCEL_WINDOW_HOURS = 6;

function canCancel(o) {
  if (!o?.created_at) return false;
  if (!CANCELLABLE.has(o.status)) return false;
  const created = new Date(o.created_at);
  const hours = (Date.now() - created.getTime()) / (1000 * 60 * 60);
  return hours <= CANCEL_WINDOW_HOURS;
}

function hoursLeft(o) {
  if (!o?.created_at) return 0;
  const created = new Date(o.created_at);
  const hoursSince = (Date.now() - created.getTime()) / (1000 * 60 * 60);
  return Math.max(0, CANCEL_WINDOW_HOURS - hoursSince);
}

function paymentRef(o) {
  return o?.razorpay_payment_id || o?.session_id || null;
}

export default function Account() {
  const { user, logout, refresh } = useAuth();
  const { content } = useContent();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("orders");
  const [profile, setProfile] = useState({ name: "", phone: "", company: "" });
  const [saving, setSaving] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(null);

  useEffect(() => {
    if (user) setProfile({ name: user.name || "", phone: user.phone || "", company: user.company || "" });
  }, [user]);

  const loadOrders = () => {
    api.get("/orders").then((r) => { setOrders(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { loadOrders(); }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/users/me", profile);
      await refresh();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const cancelOrder = async (o) => {
    const left = hoursLeft(o);
    if (!window.confirm(
      `Cancel order ${o.order_id}?\n\n` +
      `${o.payment_status === "paid" ? "A refund will be initiated automatically. It reaches your bank in 3-7 business days." : "No refund needed — this order was not paid."}\n\n` +
      `Cancellation window: ${left.toFixed(1)} hrs remaining.`
    )) return;
    setCancelBusy(o.order_id);
    try {
      const { data } = await api.post(`/orders/${o.order_id}/cancel`);
      const rf = data.refund || {};
      toast.success(
        rf.ok
          ? (o.payment_status === "paid" ? `Order cancelled · refund initiated via ${rf.gateway || "gateway"}` : "Order cancelled")
          : `Cancelled · refund needs manual review: ${rf.error || ""}`
      );
      loadOrders();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    } finally {
      setCancelBusy(null);
    }
  };

  const copyRef = (ref) => {
    navigator.clipboard.writeText(ref).then(() => toast.success("Payment reference copied"));
  };

  const totalSpent = useMemo(
    () => orders.filter((o) => o.payment_status === "paid").reduce((s, o) => s + (o.amount || 0), 0),
    [orders]
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      {/* Header */}
      <div className="border-b border-slate-200 pb-6 mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-2">My Account</div>
          <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight text-tmf-navy">Hello, {user?.name || "there"}</h1>
          <p className="mt-2 text-sm text-slate-600 font-mono-tmf">{user?.email}</p>
        </div>
        <Button variant="outline" onClick={logout} data-testid="account-logout" className="rounded-none border-slate-300 uppercase tracking-widest text-xs font-bold">
          <LogOut size={14} className="mr-2" /> Sign Out
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <div className="border border-slate-200 p-4">
          <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Total Orders</div>
          <div className="font-display font-black text-2xl text-tmf-navy">{orders.length}</div>
        </div>
        <div className="border border-slate-200 p-4">
          <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Lifetime Spend</div>
          <div className="font-display font-black text-2xl text-tmf-navy">{formatPrice(totalSpent, content)}</div>
        </div>
        <div className="border border-slate-200 p-4">
          <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Delivered</div>
          <div className="font-display font-black text-2xl text-green-700">{orders.filter((o) => o.status === "delivered").length}</div>
        </div>
        <div className="border border-slate-200 p-4">
          <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">In-Progress</div>
          <div className="font-display font-black text-2xl text-sky-700">{orders.filter((o) => ["processing", "pending", "shipped"].includes(o.status)).length}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 mb-6">
        <button
          data-testid="tab-orders"
          onClick={() => setTab("orders")}
          className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border-b-2 -mb-px transition-colors ${
            tab === "orders" ? "border-tmf-blue text-tmf-navy" : "border-transparent text-slate-500 hover:text-tmf-navy"
          }`}
        >
          <Package size={14} className="inline-block mr-2" /> Order History
        </button>
        <button
          data-testid="tab-profile"
          onClick={() => setTab("profile")}
          className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border-b-2 -mb-px transition-colors ${
            tab === "profile" ? "border-tmf-blue text-tmf-navy" : "border-transparent text-slate-500 hover:text-tmf-navy"
          }`}
        >
          <UserIcon size={14} className="inline-block mr-2" /> Edit Profile
        </button>
      </div>

      {/* Orders */}
      {tab === "orders" && (
        <div className="border border-slate-200">
          <div className="bg-tmf-navy text-white px-5 py-3 font-display font-bold uppercase tracking-widest text-xs flex justify-between">
            <span>Order History</span>
            <span className="text-slate-300 font-normal">{orders.length} orders</span>
          </div>
          {loading ? (
            <div className="p-8 text-center font-mono-tmf uppercase tracking-widest text-sm text-slate-500">Loading…</div>
          ) : orders.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-slate-600">No orders yet.</p>
              <Link to="/products" className="mt-4 inline-block text-tmf-blue font-bold hover:underline text-sm">Browse products →</Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {orders.map((o) => {
                const ref = paymentRef(o);
                const cancellable = canCancel(o);
                return (
                  <div key={o.order_id} data-testid={`order-${o.order_id}`} className="p-5 hover:bg-slate-50">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-start">
                      <div>
                        <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Order</div>
                        <div className="font-mono-tmf font-bold text-sm">{o.order_id}</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Placed</div>
                        <div className="text-sm">{new Date(o.created_at).toLocaleDateString()}</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Total</div>
                        <div className="font-display font-black">{formatPrice(o.amount, content)}</div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className={`self-start text-[10px] font-mono-tmf uppercase tracking-widest px-2 py-1 ${STATUS_COLORS[o.status] || STATUS_COLORS.pending}`}>{o.status}</span>
                        <span className={`self-start text-[10px] font-mono-tmf uppercase tracking-widest px-2 py-1 ${STATUS_COLORS[o.payment_status] || STATUS_COLORS.pending}`}>{o.payment_status}</span>
                      </div>
                      <div className="flex md:justify-end">
                        {cancellable && (
                          <Button
                            data-testid={`cancel-order-${o.order_id}`}
                            variant="outline"
                            disabled={cancelBusy === o.order_id}
                            onClick={() => cancelOrder(o)}
                            className="rounded-none border-red-500 text-red-600 hover:bg-red-50 uppercase tracking-widest text-[10px] font-bold h-8"
                          >
                            <XCircle size={13} className="mr-1" />
                            {cancelBusy === o.order_id ? "Cancelling…" : `Cancel · ${hoursLeft(o).toFixed(1)}h left`}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Items - clickable */}
                    <div className="mt-4 border-t border-slate-100 pt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {o.items?.map((it) => (
                        <Link
                          key={it.product_id}
                          to={`/products/${it.product_id}`}
                          data-testid={`order-item-${o.order_id}-${it.product_id}`}
                          className="flex items-center gap-3 border border-slate-200 p-2 hover:border-tmf-blue hover:bg-white group"
                        >
                          {it.image ? (
                            <img src={it.image} alt={it.name} className="w-12 h-12 object-cover shrink-0" />
                          ) : (
                            <div className="w-12 h-12 bg-slate-100 shrink-0" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-bold text-tmf-navy truncate group-hover:text-tmf-blue">{it.name}</div>
                            <div className="text-[10px] font-mono-tmf text-slate-500">×{it.quantity} · {formatPrice((it.price || 0) * (it.quantity || 1), content)}</div>
                          </div>
                          <ChevronRight size={14} className="text-slate-400 group-hover:text-tmf-blue shrink-0" />
                        </Link>
                      ))}
                    </div>

                    {/* Shipment tracking (Shiprocket) */}
                    {o.shiprocket?.awb && (
                      <div className="mt-3 pt-3 border-t border-slate-100 bg-indigo-50/60 -mx-5 px-5 py-3">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="text-[11px] font-mono-tmf text-slate-700">
                            <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-indigo-700 font-bold mb-1">Shipment</div>
                            <div>Courier: <span className="font-bold uppercase">{o.shiprocket.courier_name || "Assigned"}</span></div>
                            <div className="break-all">AWB: <span className="font-bold">{o.shiprocket.awb}</span></div>
                            {o.shiprocket.raw_status && <div>Status: <span className="font-bold uppercase text-indigo-700">{o.shiprocket.raw_status}</span></div>}
                          </div>
                          {o.shiprocket.tracking_url && (
                            <a
                              href={o.shiprocket.tracking_url}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`track-${o.order_id}`}
                              className="inline-flex items-center gap-1 border border-indigo-500 bg-white text-indigo-700 hover:bg-indigo-700 hover:text-white px-3 py-1.5 uppercase tracking-widest text-[10px] font-bold"
                            >
                              Track Package →
                            </a>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Payment reference + refund note */}
                    {(ref || o.refund) && (
                      <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] font-mono-tmf text-slate-600 flex flex-wrap gap-x-6 gap-y-1">
                        {ref && (
                          <div className="flex items-center gap-2">
                            <span className="uppercase tracking-widest text-slate-400">Payment Ref:</span>
                            <span className="font-bold text-slate-700 break-all">{ref}</span>
                            <button onClick={() => copyRef(ref)} className="text-tmf-blue hover:text-tmf-navy" title="Copy">
                              <ClipboardCopy size={12} />
                            </button>
                          </div>
                        )}
                        {o.gateway && (
                          <div><span className="uppercase tracking-widest text-slate-400">Gateway:</span> <span className="uppercase font-bold">{o.gateway}</span></div>
                        )}
                        {o.refund?.refund_id && (
                          <div><span className="uppercase tracking-widest text-slate-400">Refund:</span> <span className="font-bold text-purple-700 break-all">{o.refund.refund_id}</span></div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Profile */}
      {tab === "profile" && (
        <div className="border border-slate-200 max-w-2xl">
          <div className="bg-tmf-navy text-white px-5 py-3 font-display font-bold uppercase tracking-widest text-xs">Edit Profile</div>
          <div className="p-6 space-y-4">
            <div>
              <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Full Name</label>
              <Input
                data-testid="profile-name"
                className="rounded-none mt-1"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                placeholder="Full name"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Email (login)</label>
              <Input disabled className="rounded-none mt-1 bg-slate-50" value={user?.email || ""} />
              <div className="text-[10px] text-slate-400 mt-1 font-mono-tmf">Email is used for login and cannot be changed here.</div>
            </div>
            <div>
              <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Phone / Mobile</label>
              <Input
                data-testid="profile-phone"
                className="rounded-none mt-1"
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                placeholder="+91 98XXX XXXXX"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Company / Institution (optional)</label>
              <Input
                data-testid="profile-company"
                className="rounded-none mt-1"
                value={profile.company}
                onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                placeholder="Hospital / Clinic name"
              />
            </div>
            <Button
              data-testid="save-profile-btn"
              onClick={saveProfile}
              disabled={saving}
              className="rounded-none bg-tmf-blue hover:bg-tmf-navy uppercase tracking-widest text-xs font-bold w-full h-11"
            >
              <Save size={14} className="mr-2" /> {saving ? "Saving…" : "Save Profile"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
